import { ContractorType } from "../../components/people/contractor/Contractor"
import { Person } from "./Person"
import { Vehicle } from "./Vehicle"
import { Company } from "./Company"
import { PrivacyStatus } from './PrivacyStatus'

import { 
    NAMES_PATTERN, EMAIL_PATTERN, PHONE_NUMBER_PATTERN, STREET_ADDRES_PATTERN,
    CITY_NAME_PATTERN,
    isValid
 } from "../validation/stringRegex"


export type sortFunctionType = (p1: Person, p2: Person) => number

export type UserType = {
    id: number | string | undefined
    name: string | undefined
    surname: string | undefined
    email: string | undefined
    phone: string | undefined
    sf_account_id: string | undefined
    
    userData: Person | undefined

    people: Person[] | undefined
    vehicles: Vehicle[] | undefined
    companies: Company[] | undefined
    privacy: PrivacyStatus[] | undefined
}

export default class User {
    
    id: number | string | undefined
    name: string | undefined
    surname: string | undefined
    email: string | undefined
    phone: string | undefined
    sf_account_id: string | undefined
    
    userData: Person | undefined

    people: Person[] | undefined
    vehicles: Vehicle[] | undefined
    companies: Company[] | undefined
    privacy: PrivacyStatus[] | undefined

    constructor (data?: any) {
        this.id = data.id ?? undefined
        this.name = data.name ?? undefined
        this.surname = data.surname ?? undefined
        this.email = data.email ?? undefined
        this.phone = data.phone ?? undefined
        this.sf_account_id = data.sf_account_id ?? undefined
    
        this.userData = this.normalizePerson (data.userData, data.email, data.phone)
    
        this.people = [];

        (data.people ?? []).forEach( (peo: any) => {
            let tmp = this.normalizePerson (peo, data.email, data.phone)

            if (tmp) {
                this.people?.push(tmp)
            }
        })
        
        this.vehicles = data?.vehicles?.map( (v: any) => {
            delete v.vehicleUse
            delete v.vehicleParkingLocation
            return v
        })

        this.companies = data.companies ?? undefined
        this.privacy = data.privacy ?? undefined

        this.getPersonById = this.getPersonById.bind(this)
        this.getPeople = this.getPeople.bind(this)
    }

    private getPersonById (id: number | string | undefined): Person | undefined {
        return this.userData?.id === id
            ? this.userData
            : this.people?.find((p) => p.id === id)
    }

    private getPeople (customFilter: Function | undefined = undefined, addUserData: boolean = true, sortFunction?: sortFunctionType): Person[] {
        let people = (this.people) ? this.people : []
        let userData = (this.userData) ? [this.userData] : []

        let result: Person [] = 
            (addUserData)
                ? [ ...userData, ...people]
                : people


        result = 
            (customFilter
                ? result.filter( (p: Person) => customFilter(p) )
                : result
            )
            .map(
                (p: Person) => {
                    return {
                        email: this?.userData?.email, 
                        phone: this?.userData?.phone, 
                        ...p
                    }
                }
            )

        if(sortFunction) result.sort(sortFunction)

        return result
    }

    private normalizePerson (data: any | undefined, userEmail: string | undefined, userPhone: string | undefined) {
        if (data === undefined) {
            return undefined
        }

        let parsed : any = {}

        // normalizzo i valori ricevuti da SF
        Object.keys(data).forEach ((key, index) => {
            if (key === 'dateOfBirth' && data[key]) {
                parsed.dateOfBirth = new Date(data[key])
            }
            else if (
                key === 'bornInItaly' && 
                this.isTrue(data['bornInItaly']) && 
                (this.isUndefined( data['countryOfBirth'] ) || this.isUndefined( data['countryOfBirth']?.id )) 
            ) {
                parsed.countryOfBirth = {
                    id: '13',
                    value: 'ITALIA'
                }
            }
            else if (key === 'postalCodeOfResidence') {
                parsed.postalCodeOfResidence = this.normalizeWithZero(data[key])
            }
            else if (key === 'cityOfResidence' && (this.isUndefined( data[key]?.id ) || this.isUndefined( data[key]?.value ))) {
                parsed.cityOfResidence = undefined
            }
            else if (
                key === 'countryOfBirth' && 
                parsed.countryOfBirth === undefined && // potrei gia averlo normalizzato
                (this.isUndefined( data[key]?.id ) || this.isUndefined( data[key]?.value ))
            ) {
                parsed.countryOfBirth = undefined
            }
            else if (key === 'cityOfBirth' && (this.isUndefined( data[key]?.id ) || this.isUndefined( data[key]?.value ))) {
                parsed.cityOfBirth = undefined
            }
            else if (
                key === 'addressNumber' &&
                !this.isUndefined( data['addressNumber'] )
            ) {
                parsed.addressNumber = this.getValidAddressNumber( data['addressNumber'] )
            }
            else if (key === 'address') {
                // bonifico
                parsed.address = (data['address'] ?? '').toUpperCase()

                if (
                    !this.isUndefined( data['address'] ) &&
                    (!this.isUndefined( parsed.addressNumber ) || !this.isUndefined( data['addressNumber'] )) &&
                    !this.isUndefined( data['postalCodeOfResidence'] ) &&
                    !this.isUndefined( data['cityOfResidence'] ) && !this.isUndefined( data['cityOfResidence']?.id )
                ) {
                    // NON è detto che la bonifica del civico avvenga prima di quella del indirizzo!
                    const number = parsed.addressNumber ??  this.getValidAddressNumber( data['addressNumber'] )

                    parsed.testAddress = (
                        data['address'] + ', ' + number + ', ' + 
                        this.normalizeWithZero(data['postalCodeOfResidence']) + ' ' + data['cityOfResidence']?.id + ', ITALIA'
                    ).toUpperCase()
                    
                }
            }
            else if (key === 'email' && this.isUndefined( data[key] )) {
                parsed.email = userEmail
            }
            else if (key === 'phone' && this.isUndefined( data[key] )) {
                parsed.phone = userPhone
            }

            // in ogni caso
            if (this.isUndefined( data[key] )) {
                parsed[key] = undefined
            }
        })

        parsed = {
            ...data,
            ...parsed
        }

        // segnalo gli errori
        const errors : any[] = []

        if (!this.isValidName(parsed.name)) {
            errors.push('Nome')
        }
        if (!this.isValidName(parsed.surname)) {
            errors.push('Cognome')
        }
        if (!this.isValidEmail(parsed.email)) {
            errors.push('Email non valida')
        }
        if (!this.isValidPhone(parsed.phone)) {
            errors.push('Telefono')
        }
        if (!this.isValidAddress(parsed.testAddress)) {
            errors.push('Indirizzo')
        }
        if (parsed.dateOfBirth === undefined) {
            errors.push('Data di nascita')
        }
        if (parsed.gender === undefined) {
            errors.push ('Sesso')
        }

        if (!this.isTrue(parsed.bornInItaly) && (parsed.countryOfBirth === undefined || parsed.countryOfBirth.id === undefined)) {
            errors.push('Stato di nascia')
        }
        else if (
            this.isTrue(parsed.bornInItaly) && 
            (
                parsed.cityOfBirth === undefined || 
                !this.isValidCityOfBird(parsed.cityOfBirth.id)
            )
        ) {
            errors.push('Comune di nascia')
        }
        if (parsed.profession === undefined) {
            errors.push('Professione')
        }

        if (errors.length > 0) {
            parsed = {
                ...parsed,
                ...{errors: errors}
            }
        }

        return parsed
    }

    /**
     * SF invia il civico SPORCO!! (deve fare qualche concat a cazzo...)
     * La funzione bonifica il civico
     * @param addressNumber il civico da bonificare
     */
    private getValidAddressNumber (addressNumber: string) {
        const tmp = addressNumber.split(',')

        return (tmp && tmp.length > 0) 
            ? tmp[0]?.trim()
            : addressNumber
    }

    private isUndefined (v: any) {
        return v === undefined || v === '' || v === null
    }

    private isTrue (v: any) {
        return v === true || v === 'true'
    }

    private normalizeWithZero (value: string | number | undefined) {
        if (value === undefined || value === null || value === '') {
            return undefined
        }

        let normal = (typeof value === 'number') ? value.toString() : value

        return (normal.length < 5) ? normal.padStart(5, "0") : normal
    }

    private isValidName (v: string | undefined) {
        return isValid(v, NAMES_PATTERN)
    }
    private isValidEmail (v: string | undefined) {
        return isValid(v, EMAIL_PATTERN)
    }
    private isValidPhone (v: string | undefined) {
        return isValid(v, PHONE_NUMBER_PATTERN)
    }
    private isValidAddress (v: string | undefined) {
        return isValid(v, STREET_ADDRES_PATTERN) && (v as String)?.endsWith('ITALIA')
    }
    private isValidCityOfBird (v: string | undefined) {
        return isValid(v, CITY_NAME_PATTERN)
    }
}

export function getSfIdForSubmit (contractor: ContractorType, user: UserType | undefined) {
    return (user === undefined)
            ?   {}
            :   {
                sfUserId: user.id?.toString(),
                sfContractorPersonId: contractor.selectPeopleId?.toString()
            }
}